import React from "react";
import { TRUST_SESSION_SMS_VERBAL_CONSENT_SCRIPT } from "../../constants/trustSessionSms";

const lastUpdated = "March 12, 2026";

export default function VerbalConsentSmsPolicy() {
  return (
    <article className="legal-card" aria-label="ClearBadge verbal consent for SMS">
      <p className="legal-updated">Last updated: {lastUpdated}</p>
      <section className="legal-section" id="sms-terms">
        <h1>How Verbal Consent for SMS Trust Session Invitations Works with ClearBadge</h1>
        <p>
          ClearBadge helps verified professionals send secure, one-time trust session links by SMS during a live
          customer interaction. This page explains how verbal consent is collected before any SMS trust session
          invitation is sent.
        </p>
        <p>
          ClearBadge trust session invitations are transactional only. They are used to help a consumer join a live
          trust session, such as a one-way screen share or camera view, during an active interaction with a verified
          professional.
        </p>

        <hr />

        <h2>The Verbal Opt-In Process</h2>
        <p>When using ClearBadge, the typical SMS invitation flow works like this:</p>
        <ol className="legal-list">
          <li>A verified ClearBadge professional is already on a live phone call or active interaction with a consumer.</li>
          <li>
            The professional determines that it would be helpful to send a secure ClearBadge trust session link by SMS
            so the consumer can view the live trust session on their own device.
          </li>
          <li>
            Before any message is sent, the professional asks for consent using the script displayed in the ClearBadge
            application.
          </li>
          <li>
            Only after the consumer gives an affirmative verbal response does the professional enter the consumer&apos;s
            mobile number into ClearBadge and send the one-time trust session invitation.
          </li>
          <li>
            ClearBadge presents a confirmation step requiring the professional to confirm that consent was obtained
            before the SMS is sent.
          </li>
        </ol>

        <h3>Consent Script Shown to the Professional</h3>
        <blockquote className="sms-consent-script">{TRUST_SESSION_SMS_VERBAL_CONSENT_SCRIPT}</blockquote>

        <p>The message is sent only after the consumer has verbally agreed to receive it.</p>
        <p>Only a single SMS is sent for that trust session request.</p>
        <p>No marketing or promotional content is included in the message.</p>

        <hr />

        <h2>What the Professional Sees</h2>
        <p>
          <strong>Step 1:</strong> Enter the consumer&apos;s mobile number to send a secure ClearBadge trust session
          link.
        </p>
        <figure className="sms-step-figure">
          <img
            src="/sms-consent-1.png"
            alt="ClearBadge modal showing where a professional enters a consumer phone number to send an SMS trust session link"
            className="sms-step-image"
            loading="lazy"
          />
          <figcaption>Step 1 screenshot: Enter phone number modal.</figcaption>
        </figure>
        <p>
          <strong>Step 2:</strong> Confirm that the consumer gave verbal consent before sending the SMS.
        </p>
        <figure className="sms-step-figure">
          <img
            src="/sms-consent-2.png"
            alt="ClearBadge consent confirmation dialog requiring the professional to confirm verbal consent before sending SMS"
            className="sms-step-image"
            loading="lazy"
          />
          <figcaption>Step 2 screenshot: Confirm consent dialog.</figcaption>
        </figure>

        <hr />

        <h2>Sample SMS Message</h2>
        <pre className="sms-consent-script">
{`ClearBadge Verification: Join your secure trust session here:
https://clearbadge.org/s/abc123

You requested this one-time message. Reply STOP to opt out, HELP for help.`}
        </pre>
        <p>
          Each link is unique to the specific trust session and is intended only to help the consumer access that live
          interaction.
        </p>

        <hr />

        <h2>Compliance and Trust</h2>
        <ul className="legal-list">
          <li>No SMS invitation is sent without explicit, real-time verbal consent from the consumer.</li>
          <li>
            Consent is collected during the live interaction and confirmed by the professional inside ClearBadge before
            the message is sent.
          </li>
          <li>The SMS invitation is transactional and one-time only.</li>
          <li>ClearBadge does not use this invitation flow for marketing or recurring messaging.</li>
          <li>Consumers may reply STOP to opt out or HELP for help.</li>
        </ul>
        <p>
          ClearBadge is committed to privacy, transparency, and compliance with carrier and messaging-provider
          requirements.
        </p>

        <hr />

        <h2>Questions?</h2>
        <p>
          If you have questions about SMS trust session invitations or verbal consent, please contact ClearBadge
          support.
        </p>
      </section>
    </article>
  );
}
